import { Alert, Stack } from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';

import { LoadingButton } from '@/components/Form/LoadingButton';
import { Select } from '@/components/Form/Select';
import { Icon } from '@/components/Icon/Icon';
import { Form } from '@/components/Layout/Layout.styled';

import { ROUTE } from '@/constants/routes';

import { useAppDispatch } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { setTenant } from '@/redux/configs/actions';

import { Tenant } from '@/types/common';
import { Language } from '@/types/i18n';

import { getDataTestId } from '@/utils/browser';
import { getLanguageOptions } from '@/utils/languages';

export type LanguageSelectionFormValues = {
  language: string;
};

const LanguageSelectionForm: React.FC = () => {
  const { t, switchLanguage, i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (state?.tenant) {
      if (state?.tenant !== Tenant.Suriname) {
        navigate(`${ROUTE.ONBOARDING}/${state.tenant}`, {
          replace: true,
        });
      }
    } else {
      navigate(ROUTE.HOME, {
        replace: true,
      });
    }
  }, [navigate, state]);

  const formMethods = useForm<LanguageSelectionFormValues>({
    defaultValues: { language: Language.English },
    values: {
      language: i18n.language,
    },
    mode: 'onChange',
  });

  const onGetStarted = (language: Language) => {
    const tenant = Tenant.Suriname;
    dispatch(
      setTenant({
        tenant,
        language,
      }),
    );

    navigate(ROUTE.LOGIN_VERIFY_EMAIL, {
      state: {
        from: ROUTE.LANGUAGE_SELECTION,
        form: state?.form,
        type: state?.type,
        tenant: tenant,
      },
    });
  };

  const handleSubmit = (values: LanguageSelectionFormValues) => {
    onGetStarted(values.language as Language);
  };

  const { control } = formMethods;

  return (
    <Form
      onSubmit={formMethods.handleSubmit(handleSubmit)}
      flex={{
        xs: 1,
        lg: 'none',
      }}
    >
      <Stack direction="column" flex="1">
        <Select
          options={getLanguageOptions(Tenant.Suriname)}
          control={control}
          name="language"
          label={t('languageSelection.form.field.language.label')}
          inputProps={{
            ...getDataTestId('language-code-select-input'),
          }}
          onChange={event => {
            switchLanguage(event.target.value as Language);
          }}
          fullWidth
        />

        <Alert icon={<Icon name="info" />} severity="info" variant="outlined">
          {t('languageSelection.form.info')}
        </Alert>
      </Stack>
      <Stack direction="column" alignItems="stretch" gap={2}>
        <LoadingButton
          {...getDataTestId('language-selection-action-continue')}
          fullWidth
          type="submit"
        >
          {t('languageSelection.form.button.continue.text')}
        </LoadingButton>
      </Stack>
    </Form>
  );
};

export default LanguageSelectionForm;
